import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changePasswordActions } from "../../../di/di";
import { userChangePassword } from "../state/change_password_slice";
import { StringUtils } from "../../../../../core/utils/string_utils";
import ModalComponent from "../../../components/popups/modal_component";
import { Lock, Eye, EyeOff } from "lucide-react";
import Toast from "../../../components/popups/toast";
import { getUser } from "../state/profile_slice";

const ChangePasswordModalComponent = () => {
    const dispatch = useDispatch();
    const showChangePasswordModal = useSelector((state) => state.changePassword.showChangePasswordModal);
    const isLoading = useSelector((state) => state.changePassword.isLoading);
    const oldPasswordErrorMessage = useSelector((state) => state.changePassword.oldPasswordErrorMessage);
    const newPasswordErrorMessage = useSelector((state) => state.changePassword.newPasswordErrorMessage);
    const retypePasswordErrorMessage = useSelector((state) => state.changePassword.retypePasswordErrorMessage);
    const showSuccessToast = useSelector((state) => state.changePassword.showSuccessToast);
    const user = useSelector((state) => state.profile.user);
    
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [retypePassword, setRetypePassword] = useState("");
    const [localNewPasswordError, setLocalNewPasswordError] = useState("");
    const [localRetypePasswordError, setLocalRetypePasswordError] = useState("");
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showRetypePassword, setShowRetypePassword] = useState(false);

    useEffect(() => {
        if (!showChangePasswordModal) {
            setOldPassword("");
            setNewPassword("");
            setRetypePassword("");
            setLocalNewPasswordError("");
            setLocalRetypePasswordError("");
            setShowOldPassword(false);
            setShowNewPassword(false);
            setShowRetypePassword(false);
        }
    }, [showChangePasswordModal]);

    useEffect(() => {
        if (showSuccessToast) {
            Toast.success("Password updated successfully");
            dispatch(getUser());
        }
    }, [showSuccessToast, dispatch]);

    const handleOldPasswordChange = (e) => {
        setOldPassword(e.target.value);
    };

    const handleNewPasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        
        const validation = StringUtils.isPasswordValid(value);
        setLocalNewPasswordError(validation.isValid ? "" : validation.message);
        
        if (retypePassword && value !== retypePassword) {
            setLocalRetypePasswordError("Passwords do not match");
        } else {
            setLocalRetypePasswordError("");
        }
    };

    const handleRetypePasswordChange = (e) => {
        const value = e.target.value;
        setRetypePassword(value);
        
        if (value !== newPassword) {
            setLocalRetypePasswordError("Passwords do not match");
        } else {
            setLocalRetypePasswordError("");
        }
    };

    const toggleShowOldPassword = () => {
        setShowOldPassword(!showOldPassword);
    };

    const toggleShowNewPassword = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleShowRetypePassword = () => {
        setShowRetypePassword(!showRetypePassword);
    };

    const handleChangePassword = () => {
        if (!oldPassword) {
            return;
        }
        
        if (localNewPasswordError || localRetypePasswordError) {
            return;
        }
        
        dispatch(userChangePassword({
            userId: user._id,
            email: user.email,
            oldPassword,
            newPassword,
            confirmPassword: retypePassword
        }));
    };

    const isButtonDisabled = !oldPassword || !newPassword || !retypePassword || 
                            !!localNewPasswordError || !!localRetypePasswordError || 
                            isLoading;

    return (
        <ModalComponent
            isOpen={showChangePasswordModal}
            onClose={() => dispatch(changePasswordActions.setShowChangePasswordModal(false))}
            title="Change Password"
            confirmText={isLoading ? "Updating..." : "Update Password"}
            onConfirm={handleChangePassword}
            isConfirmDisabled={isButtonDisabled || isLoading}
            isLoading={isLoading}
            confirmButtonClassName={`px-6 py-2.5 text-white rounded-md transition-all ${
                isButtonDisabled || isLoading
                ? 'bg-red-900/50 cursor-not-allowed opacity-60' 
                : 'bg-gradient-to-r from-red-800 via-red-900 to-red-950 hover:opacity-90 shadow-md'
            }`}
            cancelButtonClassName="px-6 py-2.5 text-white bg-slate-900/70 backdrop-blur-sm hover:bg-red-900/40 transition-colors border border-red-900/30 rounded-md active:translate-y-0.5 active:shadow-none"
        >
            {/* Hidden dummy password field to prevent browser autofill */}
            <input type="password" autoComplete="new-password" style={{ display: 'none' }} />
            <p className="text-base text-pink-300/80 mb-6">Please enter your current password and choose a new password.</p>
            
            <div className="space-y-8">
                <div>
                    <label className="block text-sm text-pink-200 mb-2">Current Password</label>
                    <div className="relative">
                        <div className="z-[1] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Lock size={18} className="text-pink-400" />
                        </div>
                        <input 
                            type={showOldPassword ? "text" : "password"} 
                            value={oldPassword}
                            onChange={handleOldPasswordChange}
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            className={`text-base w-full pl-10 pr-10 py-3 bg-slate-950/80 backdrop-blur-sm rounded-lg text-white placeholder-pink-300/50 transition-all shadow-inner shadow-black/20 focus:outline-none focus:ring-2 focus:ring-red-900/50 ${oldPasswordErrorMessage ? 'border-red-500' : 'border-red-900/30 focus:border-red-800'}`}
                            placeholder="Enter your current password"
                        />
                        <button 
                            type="button"
                            onClick={toggleShowOldPassword}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-pink-400 hover:text-pink-300 transition-colors"
                        >
                            {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        {oldPasswordErrorMessage && (
                            <div className="absolute top-full left-0 mt-1 text-red-500 text-xs">
                                {oldPasswordErrorMessage}
                            </div>
                        )}
                    </div>
                </div>
                
                <div>
                    <label className="block text-sm text-pink-200 mb-2">New Password</label>
                    <div className="relative">
                        <div className="z-[1] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Lock size={18} className="text-pink-400" />
                        </div>
                        <input 
                            type={showNewPassword ? "text" : "password"} 
                            value={newPassword}
                            onChange={handleNewPasswordChange}
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            className={`text-base w-full pl-10 pr-10 py-3 bg-slate-950/80 backdrop-blur-sm rounded-lg text-white placeholder-pink-300/50 transition-all shadow-inner shadow-black/20 focus:outline-none focus:ring-2 focus:ring-red-900/50 ${(localNewPasswordError || newPasswordErrorMessage) ? 'border-red-500' : 'border-red-900/30 focus:border-red-800'}`}
                            placeholder="Enter your new password"
                        />
                        <button 
                            type="button"
                            onClick={toggleShowNewPassword}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-pink-400 hover:text-pink-300 transition-colors"
                        >
                            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        {(localNewPasswordError || newPasswordErrorMessage) && (
                            <div className="absolute top-full left-0 mt-1 text-red-500 text-xs">
                                {localNewPasswordError || newPasswordErrorMessage}
                            </div>
                        )}
                    </div>
                </div>
                
                <div>
                    <label className="block text-sm text-pink-200 mb-2">Confirm New Password</label>
                    <div className="relative">
                        <div className="z-[1] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Lock size={18} className="text-pink-400" />
                        </div>
                        <input 
                            type={showRetypePassword ? "text" : "password"} 
                            value={retypePassword}
                            onChange={handleRetypePasswordChange}
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            className={`text-base w-full pl-10 pr-10 py-3 bg-slate-950/80 backdrop-blur-sm rounded-lg text-white placeholder-pink-300/50 transition-all shadow-inner shadow-black/20 focus:outline-none focus:ring-2 focus:ring-red-900/50 ${(localRetypePasswordError || retypePasswordErrorMessage) ? 'border-red-500' : 'border-red-900/30 focus:border-red-800'}`}
                            placeholder="Confirm your new password"
                        />
                        <button 
                            type="button"
                            onClick={toggleShowRetypePassword}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-pink-400 hover:text-pink-300 transition-colors"
                        >
                            {showRetypePassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                        {(localRetypePasswordError || retypePasswordErrorMessage) && (
                            <div className="absolute top-full left-0 mt-1 text-red-500 text-xs">
                                {localRetypePasswordError || retypePasswordErrorMessage}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ModalComponent>
    );
};

export default ChangePasswordModalComponent; 